import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import sigmaLogo from "../Images/sigma-bank-logo.png";

const DEFAULT_ROLE = '1';

function SignUp() {
    const navigate = useNavigate();
    const location = useLocation();
    const { signUp, user } = useAuth();

    const [fullname, setFullname] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [referralCode, setReferralCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [showPassword1, setShowPassword1] = useState(false);
    const [showPassword2, setShowPassword2] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const ref = params.get('ref');
        if (ref) setReferralCode(ref.toUpperCase());
    }, [location.search]);

    useEffect(() => {
        if (user) navigate('/welcome');
    }, [user, navigate]);

    const onSubmitHandler = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setMessage('');

        if (!fullname || !email || !password || !confirmPassword) {
            setError('Please complete all required fields to continue.');
            setLoading(false);
            return;
        }

        if (password !== confirmPassword) {
            setError('The passwords you entered do not match. Please try again.');
            setLoading(false);
            return;
        }

        if (password.length < 6) {
            setError('Your password must be at least 6 characters long.');
            setLoading(false);
            return;
        }

        const { user: newUser, error: signUpError } = await signUp(email, password, fullname, DEFAULT_ROLE, referralCode);

        if (signUpError) {
            if (signUpError.includes('already exists') || signUpError.includes('already registered')) {
                setError('An account with this email already exists. Please sign in or use a different email.');
            } else {
                setError(signUpError);
            }
            setLoading(false);
        } else if (newUser) {
            setMessage('Account created successfully! Please check your email to verify your account.');
            setFullname('');
            setEmail('');
            setPassword('');
            setConfirmPassword('');
            setTimeout(() => navigate('/sign-in'), 3000);
        }
    };

    return (
        <main className="main-sectionWrapper">
            <div id="main-section" style={styles.mainSection}>
                <div style={styles.background}></div>

                {/* Desktop notice */}
                <div style={styles.desktopNotice} className="desktop-notice-signup">
                    <div style={styles.noticeCard}>
                        <div style={styles.noticeLogoCircle}>
                            <img src={sigmaLogo} alt="Sigma Bank" style={styles.noticeLogoImg} />
                        </div>
                        <h2 style={styles.noticeTitle}>Mobile Access Required</h2>
                        <p style={styles.noticeText}>
                            User accounts can only be created and accessed through mobile devices.
                            Please use your smartphone or tablet to sign up.
                        </p>
                        <div style={styles.noticeDivider}></div>
                        <p style={styles.noticeSubtext}>
                            <i className="bx bx-info-circle" style={styles.noticeInfoIcon}></i>
                            Are you an administrator?
                        </p>
                        <Link to="/admin/login" style={styles.adminLinkButton}>
                            <i className="bx bx-shield-alt-2"></i>
                            <span>Go to Admin Login</span>
                            <i className="bx bx-right-arrow-alt"></i>
                        </Link>
                        <Link to="/" style={styles.backHomeLink}>
                            <i className="bx bx-arrow-back"></i>
                            <span>Back to Home</span>
                        </Link>
                    </div>
                </div>

                {/* Mobile form */}
                <div style={styles.container} className="mobile-signup-form">
                    <div style={styles.brandContainer}>
                        <div style={styles.logoCircle}>
                            <img src={sigmaLogo} alt="Sigma Bank" style={styles.logoImage} />
                        </div>
                        <p style={styles.brandTagline}>Secure Digital Banking</p>
                    </div>

                    <div style={styles.authCard}>
                        <div style={styles.welcomeSection}>
                            <h2 style={styles.welcomeTitle}>Create Account</h2>
                            <p style={styles.welcomeSubtitle}>Join Sigma Bank and start your crypto journey</p>
                        </div>

                        <form onSubmit={onSubmitHandler} style={styles.form}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>
                                    <i className="bx bx-user" style={styles.labelIcon}></i>
                                    Full Name
                                </label>
                                <input
                                    type="text"
                                    placeholder="Enter your full name"
                                    value={fullname}
                                    onChange={(e) => setFullname(e.target.value)}
                                    disabled={loading}
                                    style={styles.input}
                                />
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}>
                                    <i className="bx bx-envelope" style={styles.labelIcon}></i>
                                    Email Address
                                </label>
                                <input
                                    type="email"
                                    placeholder="Enter your email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={loading}
                                    style={styles.input}
                                />
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}>
                                    <i className="bx bx-lock-alt" style={styles.labelIcon}></i>
                                    Password
                                </label>
                                <div style={styles.passwordWrapper}>
                                    <input
                                        type={showPassword1 ? 'text' : 'password'}
                                        placeholder="Create a strong password (min. 6 chars)"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        disabled={loading}
                                        style={styles.input}
                                    />
                                    <button type="button" onClick={() => setShowPassword1(!showPassword1)} style={styles.eyeButton}>
                                        <i className={`bx ${showPassword1 ? 'bx-hide' : 'bx-show'}`}></i>
                                    </button>
                                </div>
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}>
                                    <i className="bx bx-check-shield" style={styles.labelIcon}></i>
                                    Confirm Password
                                </label>
                                <div style={styles.passwordWrapper}>
                                    <input
                                        type={showPassword2 ? 'text' : 'password'}
                                        placeholder="Re-enter your password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        disabled={loading}
                                        style={styles.input}
                                    />
                                    <button type="button" onClick={() => setShowPassword2(!showPassword2)} style={styles.eyeButton}>
                                        <i className={`bx ${showPassword2 ? 'bx-hide' : 'bx-show'}`}></i>
                                    </button>
                                </div>
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}>
                                    <i className="bx bx-gift" style={styles.labelIcon}></i>
                                    Referral Code&nbsp;<span style={{ fontWeight: 'normal', opacity: 0.5 }}>(optional)</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Enter referral code"
                                    value={referralCode}
                                    onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                                    disabled={loading}
                                    maxLength={8}
                                    style={{ ...styles.input, letterSpacing: '2px', textTransform: 'uppercase' }}
                                />
                            </div>

                            {error && (
                                <div style={styles.errorBox}>
                                    <i className="bx bx-error-circle" style={styles.errorIcon}></i>
                                    <span style={styles.errorText}>{error}</span>
                                </div>
                            )}

                            {message && (
                                <div style={styles.successBox}>
                                    <i className="bx bx-check-circle" style={styles.successIcon}></i>
                                    <span style={styles.successText}>{message}</span>
                                </div>
                            )}

                            <button
                                type="submit"
                                style={{ ...styles.submitButton, opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                                disabled={loading}
                            >
                                {loading ? (
                                    <><div style={styles.spinner}></div><span>Creating Account...</span></>
                                ) : (
                                    <><span>Create Account</span><i className="bx bx-right-arrow-alt" style={styles.buttonIcon}></i></>
                                )}
                            </button>
                        </form>

                        <div style={styles.footer}>
                            <p style={styles.footerText}>
                                Already have an account?{' '}
                                <Link to="/sign-in" style={styles.footerLink}>Sign In</Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                @media (min-width: 769px) {
                    .mobile-signup-form { display: none !important; }
                    .desktop-notice-signup { display: flex !important; }
                }
                @media (max-width: 768px) {
                    .desktop-notice-signup { display: none !important; }
                    .mobile-signup-form { display: block !important; }
                }
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </main>
    );
}

const styles = {
    mainSection: { minHeight: '100vh', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', position: 'relative', overflow: 'hidden' },
    background: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(135deg, #0e0758 0%, #1a0d4d 50%, #0e0758 100%)', zIndex: -1 },
    container: { width: '100%', maxWidth: '480px', margin: '0 auto', position: 'relative', zIndex: 1 },
    brandContainer: { textAlign: 'center', marginBottom: '25px' },
    logoCircle: { width: '90px', height: '90px', borderRadius: '50%', background: '#FFFFFF', border: '2px solid rgba(25,191,177,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 15px', boxShadow: '0 8px 25px rgba(0,0,0,0.15)' },
    logoImage: { width: '55px', height: 'auto' },
    brandTagline: { fontSize: '14px', color: 'rgba(255,255,255,0.7)', margin: 0 },
    authCard: { background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(20px)', borderRadius: '25px', padding: '30px 25px', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
    welcomeSection: { textAlign: 'center', marginBottom: '25px' },
    welcomeTitle: { fontSize: '24px', fontWeight: '700', color: '#fff', margin: '0 0 8px 0' },
    welcomeSubtitle: { fontSize: '14px', color: 'rgba(255,255,255,0.6)', margin: 0 },
    form: { display: 'flex', flexDirection: 'column', gap: '18px' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
    label: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '600', color: 'rgba(255,255,255,0.9)', marginBottom: '5px' },
    labelIcon: { fontSize: '16px', color: '#19bfb1' },
    input: { width: '100%', padding: '14px 16px', fontSize: '16px', color: '#fff', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '12px', outline: 'none', boxSizing: 'border-box' },
    passwordWrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
    eyeButton: { position: 'absolute', right: '12px', background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: '20px', cursor: 'pointer', padding: '5px', display: 'flex', alignItems: 'center' },
    errorBox: { display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(220,53,69,0.15)', border: '1px solid rgba(220,53,69,0.3)', borderRadius: '12px', padding: '12px 15px' },
    errorIcon: { fontSize: '20px', color: '#dc3545', flexShrink: 0 },
    errorText: { fontSize: '13px', color: '#ff6b7a', lineHeight: '1.5' },
    successBox: { display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(40,167,69,0.15)', border: '1px solid rgba(40,167,69,0.3)', borderRadius: '12px', padding: '12px 15px' },
    successIcon: { fontSize: '20px', color: '#28a745', flexShrink: 0 },
    successText: { fontSize: '13px', color: '#5dd47f', lineHeight: '1.5' },
    submitButton: { width: '100%', padding: '16px', fontSize: '15px', fontWeight: '600', color: '#fff', background: 'linear-gradient(135deg, #19bfb1 0%, #0e9688 100%)', border: 'none', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', boxShadow: '0 8px 20px rgba(25,191,177,0.3)', marginTop: '8px', cursor: 'pointer' },
    buttonIcon: { fontSize: '20px' },
    spinner: { width: '18px', height: '18px', border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
    footer: { textAlign: 'center', marginTop: '25px', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.1)' },
    footerText: { fontSize: '14px', color: 'rgba(255,255,255,0.7)', margin: 0 },
    footerLink: { color: '#19bfb1', fontWeight: '600', textDecoration: 'none' },
    // Desktop notice
    desktopNotice: { display: 'none', width: '100%', maxWidth: '550px', margin: '0 auto', position: 'relative', zIndex: 1, alignItems: 'center', justifyContent: 'center' },
    noticeCard: { background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)', borderRadius: '25px', padding: '50px 40px', border: '1px solid rgba(255,255,255,0.15)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center' },
    noticeLogoCircle: { width: '100px', height: '100px', borderRadius: '50%', background: '#FFFFFF', border: '2px solid rgba(25,191,177,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 25px', boxShadow: '0 10px 30px rgba(0,0,0,0.15)' },
    noticeLogoImg: { width: '60px', height: 'auto' },
    noticeTitle: { fontSize: '28px', fontWeight: '700', color: '#fff', margin: '0 0 15px 0' },
    noticeText: { fontSize: '15px', color: 'rgba(255,255,255,0.8)', lineHeight: '1.7', margin: '0 0 30px 0' },
    noticeDivider: { width: '60px', height: '3px', background: 'linear-gradient(135deg, #19bfb1 0%, #0e9688 100%)', margin: '30px auto', borderRadius: '2px' },
    noticeSubtext: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '14px', color: 'rgba(255,255,255,0.7)', margin: '0 0 20px 0' },
    noticeInfoIcon: { fontSize: '18px', color: '#19bfb1' },
    adminLinkButton: { display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '14px 28px', background: 'linear-gradient(135deg, #19bfb1 0%, #0e9688 100%)', color: '#fff', fontSize: '15px', fontWeight: '600', textDecoration: 'none', borderRadius: '12px', boxShadow: '0 8px 20px rgba(25,191,177,0.3)', marginBottom: '15px' },
    backHomeLink: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 20px', color: 'rgba(255,255,255,0.7)', fontSize: '14px', fontWeight: '500', textDecoration: 'none', marginTop: '10px' },
};

export default SignUp;
